import React, { useState, useMemo, useEffect } from 'react';
import {
  Select, MenuItem, FormControl, SelectChangeEvent, Typography, Box,
} from '@mui/material';
import RecordSeries, { ScheduleOption } from '../../models/record-series';
import DateF from '../../models/date-f';
import { RepeatInterval } from '../../models/rrule-facade';
import { Record } from '../../editor/record.types';

interface Props {
  readonly date: string;
  readonly rrule?: string | null;
  onChange: (recordSeries: Record<any>['record_series'] | null) => void;
}

const Repeat: React.FC<Props> = ({ date, rrule, onChange }) => {
  const [recordSeries, setRecordSeries] = useState(
    new RecordSeries({
      date: DateF.isValidDate(date) ? new DateF(date) : undefined,
      rrule,
    }),
  );

  useEffect(() => {
    const newDate = DateF.isValidDate(date) ? new DateF(date) : undefined;
    const newRecordSeries = recordSeries.update({ date: newDate, rrule });
    setRecordSeries(newRecordSeries);
    if (!newDate?.isEqualTo(recordSeries?.date)) {
      onChange({ rrule: newRecordSeries.rrule });
    }
  }, [date, rrule]);

  const { schedules, selectedOption } = useMemo(() => ({
    schedules: recordSeries.getScheduleOptions(),
    selectedOption: recordSeries.getSelectedOptionValue(),
  }), [recordSeries]);

  const handleMenuItemClick = (event: SelectChangeEvent<unknown>) => {
    const newRRule = recordSeries.getRRuleFromInterval(event.target.value as RepeatInterval);
    onChange({ rrule: newRRule });
  };

  return (
    <Box display="flex" alignItems="center">
      <Typography mr={2} color="text.secondary">
        Repeat:
      </Typography>
      <FormControl sx={{ width: 350 }}>
        <Select
          value={selectedOption}
          size="small"
          variant="outlined"
          onChange={handleMenuItemClick}
        >
          {schedules.map(({ value, label }: ScheduleOption, idx: number) => (
            <MenuItem key={value} value={value} divider={idx === 3}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default Repeat;
